import { useCallback } from 'react';
import { chatService } from 'services/chat/chatService';

/**
 * Hook to enrich offers with channel IDs.
 * This hook provides a utility to fetch and add channel IDs to offer objects
 * based on request_id and user_id.
 */
export const useEnrichOffersWithChannelIds = () => {
	/**
	 * Enriches an array of offers with channel IDs by fetching them from the chat service
	 * @param offers Array of offers to enrich
	 * @param setOffers Function to update the offers state
	 * @returns Promise that resolves when all offers are enriched
	 */
	const enrichOffersWithChannelIds = useCallback(
		async <T extends { request_id?: string; farmer_id?: string; channelId?: string }>(
			offers: T[],
			setOffers: (offers: T[]) => void
		): Promise<void> => {
			if (!offers.length) return;

			try {
				// Filter offers that have request_id and farmer_id
				const eligibleOffers = offers.filter((offer) => offer.request_id && offer.farmer_id);

				if (!eligibleOffers.length) return;

				// Clone the original offers array to avoid mutating the original
				const enrichedOffers = [...offers];

				// Fetch channel IDs for all eligible offers
				await Promise.all(
					eligibleOffers.map(async (offer) => {
						try {
							// Find the corresponding offer in the cloned array
							const offerToUpdate = enrichedOffers.find(
								(o) => o.request_id === offer.request_id && o.farmer_id === offer.farmer_id
							);

							if (offerToUpdate && offer.request_id && offer.farmer_id) {
								const channelId = await chatService.getChannelIdByRequestAndUserId(
									offer.request_id,
									offer.farmer_id
								);
								offerToUpdate.channelId = channelId || '';
							}
						} catch (error) {
							console.error(`Error fetching channelId for request ${offer.request_id}:`, error);
						}
					})
				);

				// Update the state with the enriched offers
				setOffers([...enrichedOffers]);
			} catch (error) {
				console.error('Error enriching offers with channel IDs:', error);
			}
		},
		[]
	);

	return { enrichOffersWithChannelIds };
};
